import React, { useContext, useEffect, useState } from "react";
import { Stack, TextField } from "@mui/material";
import { StringObj } from "../../../../../../../type";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { QuestionsContext } from "../../../../../../../provider/MyContexts";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { AiCommands } from "./OptionArea";
import { ClearButton } from "../../../../../components/MyIconButton";

type Props = {
  onChange: (options: object[]) => void;
  handleDelete: (index: number) => void;
};

const defaultValue = "（ 入力欄 ）";

function SortableItem({ item, index }: { item: StringObj; index: number }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { updateOptionValue } = useContext(QuestionsContext);

  const [text, setText] = useState<string>("");

  function handleChangeText(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    e.stopPropagation();
    setText(e.target.value);
  }

  useEffect(() => {
    setText(item.text);
  }, [item.text]);

  return (
    <Stack ref={setNodeRef} style={style} marginX={0.5} flex={1}>
      <Stack
        direction={"row"}
        spacing={0.5}
        alignItems={"center"}
        justifyContent={"center"}
        marginY={0.5}
        padding={0.25}
        sx={{
          border: "1px solid transparent",
          ":hover": {
            border: "1px solid #333",
            borderRadius: 1,
          },
        }}
        // {...attributes}
        // {...listeners}
        flex={1}
      >
        <div {...attributes} {...listeners} style={{ cursor: "grab" }}>
          <DragHandleIcon fontSize="small" />
        </div>
        <TextField
          value={text}
          label={`${index + 1}`}
          size="small"
          sx={{ flex: 1 }}
          onChange={handleChangeText}
          onBlur={(e) => {
            e.preventDefault();
            e.stopPropagation();
            updateOptionValue(item.itemId, text, "text");
          }}
        />
      </Stack>
    </Stack>
  );
}

export default function SortableInputs(props: Props) {
  const { onChange, handleDelete } = props;

  const { selectedQuestion } = useContext(QuestionsContext);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = optionItems.findIndex((item) => item.id === active.id);
    const newIndex = optionItems.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(optionItems, oldIndex, newIndex);
    onChange(reordered);
  };

  const optionItems: any[] = selectedQuestion.options.map((item, index) => ({
    ...item,
    id: `${item.text}-${index}`,
    text: item.text,
  }));

  const handleAdd = () => {
    onChange([
      ...optionItems,
      {
        id: `${""}-${optionItems.length}`, // ユニークID
        text: "",
      },
    ]);
  };

  if (
    selectedQuestion.questionType.includes("FA") ||
    selectedQuestion.questionType.includes("年齢")
  ) {
    useEffect(() => {
      onChange([
        {
          id: `${defaultValue}-${0}`, // ユニークID
          text: defaultValue,
        },
      ]);
    }, []);

    return (
      <Stack marginX={0.5} flex={1}>
        <Stack
          direction={"row"}
          spacing={0.5}
          alignItems={"center"}
          justifyContent={"center"}
          marginY={0.5}
          padding={0.25}
          sx={{
            border: "1px solid transparent",
          }}
          flex={1}
        >
          <TextField
            value={
              selectedQuestion.options && selectedQuestion.options.length
                ? selectedQuestion.options[0].text
                : defaultValue
            }
            label={`入力欄`}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
      </Stack>
    );
  }

  // NA、TX（複数表示の時）
  return (
    <Stack>
      <AiCommands handleAdd={handleAdd} area="inputs" />
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          strategy={verticalListSortingStrategy}
          items={optionItems.map((item) => item.id)}
        >
          <Stack width={"100%"}>
            {optionItems.map((item, index) => {
              return (
                <Stack
                  marginX={1}
                  direction={"row"}
                  spacing={0.25}
                  key={item.id}
                  alignItems={"center"}
                  justifyContent={"center"}
                  width={"100%"}
                >
                  <SortableItem key={item.id} item={item} index={index} />
                  <ClearButton onClick={() => handleDelete(index)} />
                </Stack>
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>
    </Stack>
  );
}
